<template>
  <div
    class="card"
    :data-test-id="production._id"
    @click="$emit('click', production)"
    @dblclick="$emit('dblclick', production)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div class="card__content">
      <div class="card__header">
        <div
          class="card__status-indicator"
          :style="{ '--status-dot-color': production.connected ? 'var(--tgb-success, #00D483)' : '#E65447' }"
          :title="production.connected ? 'Active' : 'Inactive'"
        >
          <span class="card__status-dot" aria-hidden="true" />
          {{ production.connected ? 'Active' : 'Inactive' }}
        </div>

        <div class="card__actions">
          <label
            v-if="!isDeletedTab"
            data-entity="producer-dashboard-starProduction"
            @click.prevent.stop="toggleStar"
            class="cursor-pointer"
          >
            <span class="tgb-sr-only">Star {{ production.title }}</span>

            <input
              type="checkbox"
              class="tgb-sr-only"
              :checked="production.flagged"
            >

            <span
              class="fa-star"
              :class="[production.flagged ? 'fas' : 'far', 'text-producer-primary']"
            />
          </label>

          <dropdown position="bottom right" :classes="dropdownClasses">
            <template #trigger>
              <i
                class="fas fa-ellipsis-h tgb-icon-ellipsis cursor-pointer"
                data-entity="producer-dashboard-productionContext"
              ></i>
            </template>

            <template #dropdown-body>
              <ContextMenu :dropDownMenu="true" :actions="menuItems">
                <template v-slot="action">
                  <button
                    class="tgbContextMenuButton"
                    :data-entity="`producer-dashboard-${action.key}`"
                    @click="dropDownMenuItemClicked(action.callbackName, action.trigger)"
                  >
                    <span
                      v-if="action.icon"
                      class="fas"
                      :class="action.icon"
                    />

                    {{ action.text }}
                  </button>
                </template>
              </ContextMenu>
            </template>
          </dropdown>
        </div>
      </div>

      <h2 class="card__title" :title="production.title">
        {{ production.title }}
      </h2>

      <!-- In Production + Team Row -->
      <div class="card__meta card__meta--top">
        <div v-if="!isDeletedTab" class="card__meta-group overflow-visible">
          <label class="card__meta-label">
            In Production
          </label>

          <AvatarList :users="usersInProduction" :max="2">
            <!-- Blank when no users -->
            <template #noUsers>
              <div class="card__empty-users-list" />
            </template>
          </AvatarList>
        </div>

        <div v-if="hasTeams" class="card__meta-group card__meta-group--shrink">
          <label class="card__meta-label">
            Team
          </label>

          <img
            class="card__avatar"
            :src="ownedBy.avatar"
            :alt="ownedBy.displayName"
            @error="$event.target.src=fallbackAvatar"
          >
        </div>
      </div>

      <!-- Modified + Created Row -->
      <div class="card__meta card__meta--bottom">
        <div class="card__meta-group">
          <!-- Modified shows name + date only, no avatar -->
          <label class="card__meta-label">
            Modified
          </label>

          <div class="card__meta-text">
            {{ modifiedBy.displayName }}
          </div>

          <div class="card__meta-date">
            {{ modifiedAt }}
          </div>
        </div>

        <div
          class="card__meta-group card__meta-group--shrink"
          :title="`${createdBy.displayName} - ${createdAt}`"
        >
          <!-- Created just shows the avatar and no name -->
          <label class="card__meta-label">
            Created
          </label>

          <img
            class="card__avatar"
            :src="createdBy.avatar"
            :alt="createdBy.displayName"
            @error="$event.target.src=fallbackAvatar"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { format } from 'date-fns';

import { DefaultOptions, DeletedOptions } from './constants';

import AvatarList from '../user-avatar/AvatarList.vue';
import ContextMenu from '../context-menu/ContextMenu.vue';

const FallbackAvatar = './producer/assets/img/profile_placeholder.png';

export default {
  name: 'DirectoryCard',
  emits: ['click', 'dblclick', 'contextmenu', 'dir-item-starred', 'dropdown-menu-item-clicked'],

  components: {
    AvatarList,
    ContextMenu,
  },

  props: {
    production: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters(['hasTeams']),

    usersInProduction() {
      const { byProduction } = this.$store.state.clients;
      const users = byProduction[this.production._id] || [];
      return users;
    },

    /**
    * Metadata for created by user
    */
    createdBy() {
      const { createdBy } = this.production;

      return {
        ...createdBy,
        avatar: createdBy?.profileImageUrl || FallbackAvatar,
      };
    },

    createdAt() {
      const { created } = this.production;
      return created ? format(created, 'MM/dd/yyyy') : '';
    },

    /**
    * Metadata for owner
    */
    ownedBy() {
      const { ownedBy } = this.production;

      return {
        ...ownedBy,
        avatar: ownedBy?.profileImageUrl || FallbackAvatar,
      };
    },

    modifiedBy() {
      const { modifiedBy } = this.production;
      return modifiedBy || {};
    },

    modifiedAt() {
      const { modified } = this.production;
      return modified ? format(modified, 'MM/dd/yyyy') : '';
    },

    isDeletedTab() {
      return this.$route.params?.tab === 'deleted';
    },

    menuItems() {
      return this.isDeletedTab ? DeletedOptions : DefaultOptions;
    },
  },

  data() {
    return {
      fallbackAvatar: FallbackAvatar,
      dropdownClasses: 'dropdown-menu-w-a dropdown-menu-p-0',
    };
  },

  methods: {
    toggleStar() {
      this.$emit('dir-item-starred', this.production);
    },

    dropDownMenuItemClicked(callbackName, trigger) {
      this.$emit('dropdown-menu-item-clicked', { callbackName, trigger });
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  margin: 0;
  padding: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  border: 1px solid var(--tgb-surface-default-color);
  border-radius: 2px;
  background-color: var(--tgb-surface-default-color);
}

.card.cardActive {
  background-color: var(--tgb-directory-card-hover);
  color: var(--tgb-primary);
  border-color: var(--tgb-primary);
}

.card:hover {
  background-color: var(--tgb-directory-card-hover);
  color: var(--tgb-primary);
}

.card__content {
  display: flex;
  flex-direction: column;
  width: 100%;
  user-select: none;
}

.card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.card__status-indicator {
  position: relative;
  display: flex;
  align-items: center;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
}

.card__status-dot {
  flex: none;
  width: 0.5rem;
  height: 0.5rem;
  margin-right: 0.5rem;
  border-radius: 100%;
  background-color: var(--status-dot-color, var(--tgb-success, #00D483));
}

.card__actions {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
}

.card__title {
  display: -webkit-box;
  overflow: hidden;
  min-height: 3em;
  font-family: Barlow, sans-serif;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 1.5em;
  word-wrap: anywhere;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.card__meta {
  display: flex;
  gap: 1rem;
}

.card__meta--top {
  margin-bottom: 1rem;
}

.card__empty-users-list {
  min-height: 32px;
}

.card__meta-group {
  min-width: 0;
  flex: 1 1 0;
}

.card__meta-group--shrink {
  flex: 0 0 auto;
  min-width: 3.25rem;
}

.card__meta-label {
  display: block;
  margin-bottom: 0.25rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.625rem;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.1em;
  text-transform: none;
}

.card__meta-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card__meta-date {
  font-size: 0.6875rem;
}

.card__avatar {
  display: block;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 100%;
}
</style>
